import { useMemo } from 'react'
import { SpacingToken, Text } from '@livechat/design-system-react-components'
import { css } from '@emotion/react'

import type { IApplicationWidget } from '@/apis/apps/interfaces'
import type { Slide } from '@/components/Carousel/interfaces'
import { Carousel } from '@/components/Carousel/Carousel'
import { CarouselItem } from '@/components/Carousel/CarouselItem'
import useParamSelector from '@/hooks/useParamSelector'
import {
  getAlternativeFullscreenPath,
  isAppInReview,
  isAppInstalled,
  isPrivateApp,
  isReportApp,
} from '@/store/selectors'
import { WIDGET_CARD_DICTIONARY, WIDGET_CARD_ORDER } from '@/constants/products'
import { getWidgetCardButton, sortOn } from '@/utils/products'
import { PRODUCT } from '@/utils'
import { CAROUSEL_TYPES } from '@/constants'

import WidgetCard from './WidgetCard'

const carouselCss = css`
  ul {
    column-gap: var(${SpacingToken.Spacing5});
    align-items: stretch;
  }
`

const slideCss = css`
  display: flex;
  flex: 0 0 320px;
`

const componentWrapperCss = css`
  display: contents;
`

const placementTextCss = css`
  margin: var(${SpacingToken.Spacing5}) 0;
`

interface Props {
  widgets: IApplicationWidget[]
  appId: string
  slug: string
  designation: string
}

const CardList = ({ widgets, appId, slug, designation }: Props) => {
  const isPrivateApplication = useParamSelector(isPrivateApp, appId)
  const isInReview = useParamSelector(isAppInReview, appId)
  const isInstalled = useParamSelector(isAppInstalled, appId)
  const alternativeFullscreenWidgetPath = useParamSelector(
    getAlternativeFullscreenPath,
    appId
  )

  const isReportApplication = useParamSelector(isReportApp, appId)

  if (isReportApplication) {
    widgets?.push({
      id: 'reportApp',
      placement: 'reports',
    })
  }

  const sortedWidgets = useMemo(
    () => widgets.sort(sortOn('placement', WIDGET_CARD_ORDER)),
    [widgets]
  )

  const widgetCardSlides = useMemo(
    (): Slide[] =>
      sortedWidgets?.map(({ id: widgetId, placement }) => {
        const buttonOptions = getWidgetCardButton({
          appId,
          isInReview,
          isPrivateApplication,
          placement,
          designation,
          slug,
          widgetId,
          alternativeFullscreenWidgetPath,
        })
        const widgetCard = WIDGET_CARD_DICTIONARY[placement]
        const widgetCardProps = {
          ...widgetCard,
          buttonsOptions: [buttonOptions],
        }

        return {
          type: CAROUSEL_TYPES.PLACEMENT,
          component: <WidgetCard key={placement} {...widgetCardProps} />,
          id: widgetId,
        }
      }) || [],
    [
      alternativeFullscreenWidgetPath,
      appId,
      designation,
      isInReview,
      isPrivateApplication,
      slug,
      sortedWidgets,
    ]
  )

  if (
    !sortedWidgets?.length ||
    !isInstalled ||
    designation === PRODUCT.HelpDesk
  ) {
    return null
  }

  return (
    <div>
      <Text bold css={placementTextCss}>
        App placement
      </Text>

      <Carousel
        css={carouselCss}
        items={widgetCardSlides}
        renderItem={({ item, isSnapPoint }) => (
          <CarouselItem key={item.id} isSnapPoint={isSnapPoint} css={slideCss}>
            <div css={componentWrapperCss}>{item.component}</div>
          </CarouselItem>
        )}
      />
    </div>
  )
}

export default CardList
